import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";
import { track } from "@/lib/pixel";
import { useAuth } from "@/context/AuthContext";
import { Header } from "@/pages/CollegePredictorPage";
import { Crown, Check, Sparkles, Loader2, Zap, Star, Compass, Clock } from "lucide-react";

const TIERS = [
  {
    id: "free", name: "Free Explorer", price: "₹0", period: "forever",
    tagline: "Explore your options", icon: Compass,
    features: ["1 AI career assessment", "5 AI coach messages", "Basic college predictor", "Community access", "View counsellor profiles"],
    cta: "Current plan", highlight: false,
  },
  {
    id: "pro", name: "Pro Student", price: "₹499", period: "per month",
    tagline: "Your AI Career Intelligence", icon: Star,
    features: ["Unlimited AI coach", "Unlimited predictors", "Full college comparison", "Rank predictor", "Resume builder", "1 counsellor call / month"],
    cta: "Upgrade to Pro", highlight: true,
  },
  {
    id: "premium", name: "Premium Career Pass", price: "₹999", period: "per month",
    tagline: "AI + Priority Human Guidance", icon: Crown,
    features: ["Everything in Pro", "Priority counsellor booking", "Scholarship finder", "PDF career blueprint", "Personalized roadmap", "Live webinars"],
    cta: "Go Premium", highlight: false,
  },
];

export default function PricingPage() {
  const { user, updateUser, refresh } = useAuth();
  const [loading, setLoading] = useState("");
  const [payEnabled, setPayEnabled] = useState(false);
  const [couponInput, setCouponInput] = useState("");
  const [coupon, setCoupon] = useState(null); // {code, discount, label}
  const [checking, setChecking] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0); // ms remaining on the win-back offer
  const [termsOk, setTermsOk] = useState(true);
  const [mktOptin, setMktOptin] = useState(false);

  const PRICE = { free: 0, pro: 499, premium: 999 };

  const applyCoupon = async (raw) => {
    const code = (raw || "").trim().toUpperCase();
    if (!code) return;
    setChecking(true);
    try {
      const { data } = await api.get(`/subscription/coupon/${code}`, { params: { plan: "premium" } });
      if (data.valid) {
        setCoupon({ code: data.code, discount: data.discount, label: data.label, expiresAt: data.expires_at });
        setTimeLeft((data.expires_in || 0) * 1000);
        toast.success(`Coupon applied — ${data.label}!`);
      } else {
        setCoupon(null); setTimeLeft(0);
        toast.error(data.reason || "Invalid coupon code");
      }
    } catch {
      setCoupon(null); setTimeLeft(0);
      toast.error("Could not apply coupon");
    } finally {
      setChecking(false);
    }
  };

  useEffect(() => {
    if (!coupon?.expiresAt) return;
    const end = new Date(coupon.expiresAt).getTime();
    const tick = () => {
      const rem = end - Date.now();
      if (rem <= 0) { setCoupon(null); setTimeLeft(0); setCouponInput(""); toast.info("The win-back offer has expired"); }
      else setTimeLeft(rem);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [coupon]);

  const fmtTime = (ms) => {
    const s = Math.max(0, Math.floor(ms / 1000));
    const h = String(Math.floor(s / 3600)).padStart(2, "0");
    const m = String(Math.floor((s % 3600) / 60)).padStart(2, "0");
    const sec = String(s % 60).padStart(2, "0");
    return `${h}:${m}:${sec}`;
  };

  useEffect(() => {
    api.get("/subscription/config").then((r) => setPayEnabled(r.data.enabled)).catch(() => {});
    const code = new URLSearchParams(window.location.search).get("code");
    if (code) { setCouponInput(code.toUpperCase()); applyCoupon(code); }
  }, []);

  const finishUpgrade = async (plan) => {
    updateUser({ plan });
    await refresh();
    toast.success(`You're now on the ${plan} plan! +200 XP`);
  };

  const payWithRazorpay = async (plan) => {
    let order;
    try {
      ({ data: order } = await api.post("/subscription/create-order", { plan, coupon: coupon?.code, terms_accepted: termsOk, marketing_optin: mktOptin }));
    } catch (e) {
      throw new Error(e?.response?.data?.detail || "Could not start checkout");
    }
    track("InitiateCheckout", {
      value: (order.amount || 0) / 100,
      currency: order.currency || "INR",
      content_name: plan,
    }, `checkout_${order.order_id}`);
    return new Promise((resolve, reject) => {
      if (!window.Razorpay) { reject(new Error("Razorpay not loaded")); return; }
      const rzp = new window.Razorpay({
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        name: "EntranceZone AI",
        description: `${plan === "pro" ? "Pro Student" : "Premium Career Pass"} subscription`,
        order_id: order.order_id,
        prefill: { name: order.name, email: order.email },
        theme: { color: "#06B6D4" },
        handler: async (resp) => {
          try {
            await api.post("/subscription/verify", {
              plan,
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            track("Purchase", {
              value: (order.amount || 0) / 100,
              currency: order.currency || "INR",
              content_name: plan,
            }, resp.razorpay_payment_id);
            await finishUpgrade(plan);
            resolve();
          } catch (e) { reject(e); }
        },
        modal: { ondismiss: () => reject(new Error("cancelled")) },
      });
      rzp.on("payment.failed", (resp) => {
        const err = resp?.error || {};
        api.post("/subscription/payment-failed", {
          order_id: order.order_id, plan, step: "checkout",
          code: err.code, description: err.description, reason: err.reason,
        }).catch(() => {});
        reject(new Error(err.description || "Payment failed"));
      });
      rzp.open();
    });
  };

  const upgrade = async (plan) => {
    if (plan === "free") return;
    if (!termsOk) { toast.error("Please acknowledge the Subscription Terms and Privacy Notice to continue."); return; }
    setLoading(plan);
    try {
      if (payEnabled) {
        await payWithRazorpay(plan);
      } else {
        await api.post("/subscription/upgrade", { plan });
        await finishUpgrade(plan);
      }
    } catch (e) {
      if (e.message !== "cancelled") toast.error(e.message || "Payment could not be completed. Try again.");
    } finally {
      setLoading("");
    }
  };

  const consentBlock = (
    <div data-testid="checkout-consent-block" className="mb-6 space-y-2 rounded-2xl border border-white/10 bg-white/[0.02] p-4 text-xs text-slate-300">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Before you pay</p>
      <label className="flex items-start gap-2">
        <input data-testid="checkout-terms-checkbox" type="checkbox" checked={termsOk} onChange={(e) => setTermsOk(e.target.checked)} className="mt-0.5 accent-cyan-400" />
        <span>I acknowledge the <Link to="/terms" target="_blank" className="text-cyan-400 underline">Subscription Terms</Link>, <Link to="/refund" target="_blank" className="text-cyan-400 underline">Refund Policy</Link> and <Link to="/privacy" target="_blank" className="text-cyan-400 underline">Privacy Notice</Link>. Payment data is processed by Razorpay; we store only order/payment references needed for your subscription, receipts and tax records. <span className="text-slate-500">(Required)</span></span>
      </label>
      {!termsOk && <p data-testid="checkout-terms-error" className="text-rose-400">Checkout cannot proceed without this acknowledgement.</p>}
      <label className="flex items-start gap-2">
        <input data-testid="checkout-marketing-checkbox" type="checkbox" checked={mktOptin} onChange={(e) => setMktOptin(e.target.checked)} className="mt-0.5 accent-cyan-400" />
        <span>Send me offers and promotional updates by email/WhatsApp. <span className="text-slate-500">(Optional — not required to purchase; withdraw any time in Privacy & Data)</span></span>
      </label>
    </div>
  );

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-8 py-8">
      <Header icon={Crown} eyebrow="Subscription Plans"
        title="Invest in your career clarity" desc="Upgrade anytime. Cancel anytime." />

      {payEnabled ? (
        <div className="mb-4 rounded-xl border border-cyan-400/20 bg-cyan-400/5 px-4 py-3 text-xs text-cyan-300">
          Secure checkout by Razorpay. UPI, cards, net banking & wallets accepted. We never see or store your card/UPI details.
        </div>
      ) : (
        <div className="mb-4 rounded-xl border border-amber-400/20 bg-amber-400/5 px-4 py-3 text-xs text-amber-300">
          Demo mode: upgrades activate instantly without payment.
        </div>
      )}
      {consentBlock}

      {/* Coupon */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center gap-3 rounded-xl border border-white/8 bg-[#131C2E] px-4 py-3">
        <span className="text-sm font-medium text-slate-300">Have a coupon?</span>
        <div className="flex gap-2 flex-1 max-w-sm">
          <input data-testid="coupon-input" value={couponInput}
            onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
            placeholder="Enter code"
            className="flex-1 rounded-lg border border-white/10 bg-[#0B1420] px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:border-cyan-400/50 outline-none" />
          <button data-testid="coupon-apply-btn" onClick={() => applyCoupon(couponInput)} disabled={checking || !couponInput}
            className="rounded-lg bg-cyan-400 px-4 py-2 text-sm font-bold text-[#041016] disabled:opacity-50">
            {checking ? <Loader2 className="h-4 w-4 animate-spin" /> : "Apply"}
          </button>
        </div>
        {coupon && (
          <div className="flex flex-wrap items-center gap-3">
            <span data-testid="coupon-applied" className="text-sm font-semibold text-emerald-400 flex items-center gap-1">
              <Check className="h-4 w-4" /> {coupon.code} — {coupon.label} applied
            </span>
            {timeLeft > 0 && (
              <span data-testid="offer-countdown" className="flex items-center gap-1.5 rounded-full bg-amber-400/15 px-3 py-1 text-xs font-bold text-amber-300 animate-pulse">
                <Clock className="h-3.5 w-3.5" /> Offer ends in {fmtTime(timeLeft)}
              </span>
            )}
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        {TIERS.map((t, i) => {
          const isCurrent = (user?.plan || "free") === t.id;
          const hasDiscount = coupon && t.id !== "free";
          const discounted = hasDiscount ? Math.max(1, PRICE[t.id] - coupon.discount) : null;
          return (
            <motion.div key={t.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }}
              data-testid="pricing-tier-card"
              className={`relative rounded-3xl border p-7 flex flex-col ${
                t.highlight ? "border-cyan-400/40 bg-gradient-to-b from-[#0F2A33] to-[#131C2E] glow-cyan" : "border-white/8 bg-[#131C2E]"
              }`}>
              {t.highlight && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-cyan-400 px-4 py-1 text-xs font-bold text-[#041016] flex items-center gap-1">
                  <Sparkles className="h-3 w-3" /> Most Popular
                </span>
              )}
              <div className="flex items-center gap-2">
                {t.icon && (
                  <span className={`flex h-8 w-8 items-center justify-center rounded-lg ${
                    t.id === "premium" ? "bg-amber-400/15 text-amber-300" : t.id === "pro" ? "bg-cyan-400/15 text-cyan-300" : "bg-white/8 text-slate-300"}`}>
                    <t.icon className="h-4 w-4" />
                  </span>
                )}
                <h3 className="font-display text-xl font-bold">{t.name}</h3>
              </div>
              {t.tagline && (
                <p data-testid="pricing-tier-tagline" className={`mt-1.5 text-sm font-medium ${
                  t.id === "premium" ? "text-amber-300" : t.id === "pro" ? "text-cyan-300" : "text-slate-400"}`}>
                  {t.tagline}
                </p>
              )}
              <div className="mt-3 flex items-end gap-1">
                {hasDiscount ? (
                  <>
                    <span className="font-display text-4xl font-extrabold text-emerald-400" data-testid="pricing-discounted">₹{discounted}</span>
                    <span className="text-lg text-slate-500 line-through mb-1 ml-1">{t.price}</span>
                    <span className="text-sm text-slate-400 mb-1">/{t.period}</span>
                  </>
                ) : (
                  <>
                    <span className="font-display text-4xl font-extrabold">{t.price}</span>
                    <span className="text-sm text-slate-400 mb-1">/{t.period}</span>
                  </>
                )}
              </div>
              <ul className="mt-6 space-y-3 flex-1">
                {t.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-slate-300">
                    <Check className="h-4 w-4 text-emerald-400 mt-0.5 shrink-0" /> {f}
                  </li>
                ))}
              </ul>
              <button data-testid="pricing-subscribe-btn" onClick={() => upgrade(t.id)} disabled={isCurrent || loading === t.id}
                className={`mt-7 flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-bold transition-transform hover:scale-[1.02] disabled:opacity-60 disabled:hover:scale-100 ${
                  t.highlight ? "bg-cyan-400 text-[#041016]" : isCurrent ? "bg-white/5 text-slate-400" : "bg-white/10 text-white hover:bg-white/15"
                }`}>
                {loading === t.id ? <Loader2 className="h-4 w-4 animate-spin" /> : isCurrent ? "Current plan" : <><Zap className="h-4 w-4" /> {t.cta}</>}
              </button>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
